import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    subsetTotalGetter,
    SubsetTotalGetterProps,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
    subsetRemover,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { getConfig, klinikenApi } from "@/api";
import cloneDeep from "lodash/cloneDeep";

export type Aktivitet = {
    id: string;
    lag: string;
    lag_display?: { id: string; namn: string };
    plats: string;
    plats_display?: { id: string; namn: string };
    datum: string;
    tid: string;
    langd: number | null;
    aktivitetstyp: string;
    aktivitetstyp_display?: { id: string; namn: string };
    underlag: string;
    underlag_display?: { id: string; namn: string };
    beskrivning?: string;
    status: string;
};

type State = BaseState & {
    aktiviteter: Map<Aktivitet["id"], Aktivitet>;
};

const getDefaultState = (): State => ({
    aktiviteter: new Map(),
    ...cloneDeep(baseState),
});

const moduleName = "aktiviteter";
const baseUrl = "skreg/aktiviteter/";

// Serverns filter tidpunkt__date__/tidpunkt__time__ matchar fältnamnen datum/tid på objektet
const filterParamConverter = ({ filterParams }: { filterParams: FilterParams }): FilterParams => {
    const result: FilterParams = {};
    for (const [key, value] of Object.entries(filterParams)) {
        result[key.replace("tidpunkt__date__", "datum__").replace("tidpunkt__time__", "tid__")] = value;
    }
    return result;
};

export default {
    namespaced: true,

    state: getDefaultState(),

    getters: {
        aktiviteter: (state: State) => [...state.aktiviteter.values()],

        aktiviteterSubset: (state: State) => (props: SubsetGetterProps<State>) => {
            return subsetGetter<State>({
                ...props,
                state,
                collectionName: moduleName,
            });
        },

        // Antalet rader som finns för subsetet, det värde paginatorn räknar på
        aktiviteterSubsetTotal: (state: State) => (props: SubsetTotalGetterProps<State>) =>
            subsetTotalGetter<State>({ ...props, state }),

        aktivitetById:
            (state: State) =>
            ({ id }: { id?: string }) => {
                if (!id) return null;
                return state.aktiviteter.get(id);
            },
    },

    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());
                resetDelayedResetTimeout({ moduleName });
            },
        }),

        addAktiviteter: (
            state: State,
            {
                offset,
                subsetName = "default",
                aktiviteter = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                aktiviteter: Aktivitet[];
            }
        ) =>
            subsetSetter<State, Aktivitet>({
                offset,
                subsetName,
                state,
                filterParamConverter,
                collectionName: moduleName,
                items: aktiviteter,
                includeDefaultSubset: true,
            }),

        updateAktivitet: (
            state: State,
            {
                aktivitet,
                offset,
            }: {
                aktivitet: Aktivitet[];
                offset: number;
            }
        ) =>
            subsetUpdateSetter<State, Aktivitet>({
                offset,
                state,
                filterParamConverter,
                collectionName: moduleName,
                items: aktivitet,
                includeDefaultSubset: true,
            }),

        deleteAktivitet: (state: State, { aktivitetId }: { aktivitetId: string }) =>
            subsetRemover<State>({
                state,
                collectionName: moduleName,
                ids: [aktivitetId],
            }),
    },

    actions: {
        ...baseActions({ moduleName }),

        fetchAktiviteter: async (
            { commit, state }: BaseActionsProps<State>,
            {
                filterParams = {},
                config = {},
                limit = 20,
                page = 1,
                forceRefresh = false,
            }: {
                limit?: number;
                page?: number;
                filterParams?: FilterParams;
                config?: { tabbnamn?: string };
                forceRefresh?: boolean;
            } = {}
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, Aktivitet>({
                    filterParams,
                    state,
                    config,
                    page,
                    limit,
                    forceRefresh,
                    collectionName: moduleName,
                    apiUrl: baseUrl,
                });

                if (typeof total === "number") {
                    commit("setTotal", { total, subsetName });
                }

                if (!results) {
                    return { subsetName, total, nextPage };
                }

                commit("addAktiviteter", {
                    subsetName,
                    offset,
                    aktiviteter: results,
                });

                return { total, nextPage, subsetName, results };
            } catch (error) {
                console.error(error);
                throw error;
            }
        },

        createAktivitet: async (
            { commit, state }: BaseActionsProps<State>,
            { aktivitet }: { aktivitet: Partial<Aktivitet> }
        ) => {
            try {
                const response = await klinikenApi.post(baseUrl, aktivitet, getConfig({}));
                commit("addAktiviteter", {
                    subsetName: "newlyCreated",
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    aktiviteter: [response.data],
                });
                return response;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },

        updateAktivitet: async (
            { commit, state }: BaseActionsProps<State>,
            { aktivitet }: { aktivitet: Aktivitet }
        ) => {
            try {
                const response = await klinikenApi.put(`${baseUrl}${aktivitet.id}/`, aktivitet, getConfig({}));
                commit("updateAktivitet", {
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                    aktivitet: [response.data],
                });
                return response;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },

        deleteAktivitet: async ({ commit }: BaseActionsProps<State>, { aktivitet }: { aktivitet: Aktivitet }) => {
            try {
                await klinikenApi.delete(`${baseUrl}${aktivitet.id}/`, getConfig({}));
                commit("deleteAktivitet", { aktivitetId: aktivitet.id });
            } catch (error) {
                console.error(error);
                throw error;
            }
        },

        fetchAvstamningar: async (_: BaseActionsProps<State>, { aktivitetId }: { aktivitetId: string }) => {
            try {
                const response = await klinikenApi.get(`${baseUrl}${aktivitetId}/avstamningar/`, getConfig({}));
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },

        saveAvstamningar: async (
            { commit, state }: BaseActionsProps<State>,
            { aktivitetId, avstamningar }: { aktivitetId: string; avstamningar: unknown[] }
        ) => {
            try {
                const response = await klinikenApi.put(
                    `${baseUrl}${aktivitetId}/avstamningar/`,
                    avstamningar,
                    getConfig({})
                );
                const aktivitetResponse = await klinikenApi.get(`${baseUrl}${aktivitetId}/`, getConfig({}));
                commit("updateAktivitet", {
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                    aktivitet: [aktivitetResponse.data],
                });
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },

        fetchPreviousAvstamningar: async (_: BaseActionsProps<State>, { aktivitetId }: { aktivitetId: string }) => {
            try {
                const response = await klinikenApi.get(
                    `${baseUrl}${aktivitetId}/avstamningar/previous/`,
                    getConfig({})
                );
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },

        createJournalanteckning: async (_: BaseActionsProps<State>, { avstamningId }: { avstamningId: string }) => {
            try {
                const response = await klinikenApi.post(
                    `skreg/avstamningar/${avstamningId}/journalanteckningar/`,
                    {},
                    getConfig({})
                );
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
    },
};
